/**
 * @file   src\Pages\CWOEditTemplate.tsx
 * @brief  EditTemplate page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React,Container, Breadcrumb, Button, useOutletContext, useNavigate, useEffect, useState,  useParams } from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Essentials, Paragraph, Bold, Italic, Heading } from 'ckeditor5';
import ClassicEditor from '../utils/classicEditor';
import axios from '../config/axiosConfig'
import 'ckeditor5/ckeditor5.css';
import { CWOEmailTemplateApi } from '../utils/apiUrls';
import { MessageToaster } from '../utils/toastUtil';
import SmallLoader from '../components/SmallLoader';
import Carwashlogoinner from '../assets/images/CarWash-HeaderLogo.svg';
import TemplateBanner from '../assets/images/carwash-graphics.png';

const CWOEditTemplate = () => {

    const navigate = useNavigate()
    const toaster = new MessageToaster();
    const params = useParams()
    // Access the context
    const {  updateData } = useOutletContext<AppContextType>();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ TemplateName: '', EmailTemplateText: '',TemplateType:0,emailTemplateId:0 })

    useEffect(() => {
        handleUpdate();
        if (params.id)
            viewTemplate(parseInt(params.id));
    }, [])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Edit Template", url: `/${pageURLs.cwo_emailtemplates}`, showBackButton: true, showMainMenu: true, activeLink: "" });
    };

    // show template details by id
    const viewTemplate = async (id: number) => {
        try {
            setLoading(true); // Show loader
            const { data } = await axios.get(`${CWOEmailTemplateApi.VIEW_EMAIL_TEMPLATE}/${id}`);
            if (data.error === 0) {
                setFormData(data.data)
            }
        } catch (error: any) {
            console.error('Error fetching hardware list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
        finally {
            setLoading(false);
        }
    }


    // On edit
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const updateresponse = await axios.post(CWOEmailTemplateApi.UPDATE_TEMPLATE, {
                templateId: params.id ? parseInt(params.id) : 0,
                emailTemplateText: formData.EmailTemplateText,
            })
            if (updateresponse.data.error === 0) {
                toaster.toastSuccess(updateresponse?.data?.message);
                navigate("/" + pageURLs.cwo_emailtemplates)
            }
            else {
                toaster.toastError(updateresponse?.data?.message);
            }
        } catch (error) {

            console.log(error)
        }
        finally {
            setLoading(false); // Hide loader
        }
    }

    return (

        <div className='hardware-listing-container'>
            <Container>
                <Breadcrumb>
                    <Breadcrumb.Item onClick={() => navigate(`/${pageURLs.cwo_emailtemplates}`)}>Email Template</Breadcrumb.Item>
                    <Breadcrumb.Item active>Edit Template</Breadcrumb.Item>
                </Breadcrumb>
                <div className="edit-template-wrap d-flex">
                    <div className="template-block template-customizer">
                        <div className="form-inner">
                            {/* <form onSubmit={(e) => { handleSubmit(e) }}> */}
                            <div className="template-name">
                                <label>Template Name</label>
                                <h6>{formData.TemplateName}</h6>
                            </div>
                            <div className="editor-container ">
                                {!loading && formData.EmailTemplateText !== '' && (
                                    <CKEditor
                                        editor={ClassicEditor as any}
                                        config={{
                                            licenseKey: 'GPL',
                                            plugins: [Essentials, Paragraph, Bold, Italic, Heading],
                                            toolbar: [
                                                'heading', '|', 'bold', 'italic', 'underline', 'strikethrough', '|',
                                                'link', 'bulletedList', 'numberedList', 'blockQuote', '|',
                                                'undo', 'redo', '|', 'alignment', 'fontSize', 'fontColor',
                                                'fontBackgroundColor', 'highlight', 'codeBlock',
                                                'insertTable', 'imageUpload', 'mediaEmbed'
                                            ],
                                            heading: {
                                                options: [
                                                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                                                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                                                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                                                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                                                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                                                ]
                                            },
                                            image: {
                                                toolbar: [
                                                    'imageTextAlternative', 'toggleImageCaption',
                                                    'imageStyle:full', 'imageStyle:side'
                                                ]
                                            },
                                            table: {
                                                contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                                            },
                                        }}
                                        onReady={(editor) => {
                                            if (formData.EmailTemplateText) {
                                                editor.setData(formData.EmailTemplateText);
                                            }

                                        }}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            setFormData((prev) => ({
                                                ...prev,
                                                EmailTemplateText: data,
                                            }));

                                        }}
                                
                                    />)}
                            </div>
                            <div className="btn-sec d-flex justify-content-end">
                                <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/${pageURLs.cwo_emailtemplates}`)}>Cancel</Button>
                                <Button title="Save" variant="primary" className="primary-btn" onClick={() => handleSubmit()}>Save</Button>
                                {/* <Button title="Preview" variant="primary" className="primary-btn" onClick={() =>window.open(`/cwo-emailtemplate/template-${formData.TemplateType}?id=${formData.emailTemplateId}`, '_blank', 'noopener,noreferrer')}>Preview</Button> */}

                            </div>
                        </div>
                    </div>
                    <div className="template-block template-preview">
                        {formData.TemplateType=== 1 ?
                            <div className="email-template-wrap">
                                <div className="email-template mx-auto">
                                    <div className="mail-content">
                                        <div className="logo"><img src={Carwashlogoinner} width="124" height="97" alt="Car Wash App" /></div>
                                        <div className="banner"><img src={TemplateBanner} alt="Car Wash App" /></div>
                                    
                                        <h6 className='f-medium'>Hi John,</h6>
                                        {/* <h2 className='f-regular pb-3'>Thank you for choosing us! We’re thrilled to welcome you as our newest subscription member. 
                                            You can easily manage your subscription (cancel your plan, change your plan, or track your usage) through your account on our website. </h2> */}
                                        <div dangerouslySetInnerHTML={{__html: formData.EmailTemplateText}}></div>
                                        <p className='mb-1'>Your subscription details:</p>
                                        <ul>
                                            <li><span className='f-semi'>Plan:</span> #carwash name#</li>
                                            <li><span className='f-semi'>Start Date:</span> Monday August 26, 2024</li>
                                            <li><span className='f-semi'>Monthly Fee:</span> $6</li>
                                            <li><span className='f-semi'>Number of Washes:</span> 2</li>
                                        </ul>
                                        <h6 className='p-0 regards'>Best Regards, <br />
                                            <span className='f-bold'>Car Wash App team</span>
                                        </h6>
                                    </div>
                                    <div className="copyright text-center">
                                        <p className='f-regular'>Copyright © 2024 Car Wash App. All rights reserved.</p>
                                        <span className='f-light'>This is an automatic mail. Please do not reply to this email address.</span>
                                    </div>
                                    
                                </div >
                            </div>
                        : formData.TemplateType === 2 ?
                        <div className="email-template-wrap">
                            <div className="email-template mx-auto">
                                <div className="mail-content">
                                    <div className="logo"><img src={Carwashlogoinner} width="124" height="97" alt="Car Wash App" /></div>
                                    <div className="banner"><img src={TemplateBanner} alt="Car Wash App" /></div>
                                
                                    <h6 className='f-medium'>Hi John,</h6>
                                    {/* <h2 className='f-regular pb-3'>We’re sorry to see you go, but your request to cancel your subscription has been processed successfully.</h2> */}
                                    <div dangerouslySetInnerHTML={{__html: formData.EmailTemplateText}}></div>

                                    <p className='mb-1'>Cancellation details:</p>
                                    <ul>
                                        <li><span className='f-semi'>Plan:</span> #carwash name#</li>
                                    </ul>
                                    <h6 className='p-0 regards'>Best Regards, <br />
                                        <span className='f-bold'>Car Wash App team</span>
                                    </h6>
                                </div>
                                <div className="copyright text-center">
                                    <p className='f-regular'>Copyright © 2024 Car Wash App. All rights reserved.</p>
                                    <span className='f-light'>This is an automatic mail. Please do not reply to this email address.</span>
                                </div>
                                
                            </div >
                        </div>
                        : formData.TemplateType === 3 ?
                        <div className="email-template-wrap">
                            <div className="email-template mx-auto">
                                <div className="mail-content">
                                    <div className="logo"><img src={Carwashlogoinner} width="124" height="97" alt="Car Wash App" /></div>
                                    <div className="banner"><img src={TemplateBanner} alt="Car Wash App" /></div>
                                
                                    <h6 className='f-medium'>Hi John,</h6>
                                    {/* <h2 className='f-regular pb-3'>We’re thrilled to let you know that your subscription has been renewed successfully. You can easily manage your subscription 
                                        (cancel your plan, change your plan, or track your usage) through your account on our website.</h2> */}
                                    <div dangerouslySetInnerHTML={{__html: formData.EmailTemplateText}}></div>
                                    <p className='mb-1'>Your subscription  details:</p>
                                    <ul>
                                        <li><span className='f-semi'>Plan:</span> #carwash name#</li>
                                        <li><span className='f-semi'>Start Date:</span> Monday August 26, 2024</li>
                                        <li><span className='f-semi'>Monthly Fee:</span> $6</li>
                                        <li><span className='f-semi'>Number of Washes:</span> 2</li>
                                    </ul>
                                    <h6 className='p-0 regards'>Best Regards, <br />
                                        <span className='f-bold'>Car Wash App team</span>
                                    </h6>
                                </div>
                                <div className="copyright text-center">
                                    <p className='f-regular'>Copyright © 2024 Car Wash App. All rights reserved.</p>
                                    <span className='f-light'>This is an automatic mail. Please do not reply to this email address.</span>
                                </div>
                                
                            </div >
                        </div>
                        :formData.TemplateType === 4 ?
                        <div className="email-template-wrap">
                            <div className="email-template mx-auto">
                                <div className="mail-content">
                                    <div className="logo"><img src={Carwashlogoinner} width="124" height="97" alt="Car Wash App" /></div>
                                    <div className="banner"><img src={TemplateBanner} alt="Car Wash App" /></div>
                                
                                    <h6 className='f-medium'>Hi John,</h6>
                                    {/* <h2 className='f-regular pb-3'>We’re reaching out to inform you that, unfortunately, your recent attempt to renew your subscription has failed.</h2> */}
                                    <div dangerouslySetInnerHTML={{__html: formData.EmailTemplateText}}></div>
                                    <p className='mb-1'>Your subscription  details:</p>
                                    <ul>
                                        <li><span className='f-semi'>Plan:</span> #carwash name#</li>
                                        <li><span className='f-semi'>Monthly Fee:</span> $6</li>
                                    </ul>
                                    <h6 className='p-0 regards'>Best Regards, <br />
                                        <span className='f-bold'>Car Wash App team</span>
                                    </h6>
                                </div>
                                <div className="copyright text-center">
                                    <p className='f-regular'>Copyright © 2024 Car Wash App. All rights reserved.</p>
                                    <span className='f-light'>This is an automatic mail. Please do not reply to this email address.</span>
                                </div>
                                
                            </div >
                        </div>
                        :''
                        }
                    </div>
                </div>
            </Container >
            {loading && <SmallLoader />}
        </div >
    )
}

export default CWOEditTemplate;
